import { cn } from '@goaltree/ui/lib/utils'
import { Link } from '@tanstack/react-router'
import { Check, ChevronLeft, ChevronRight } from 'lucide-react'
import { Fragment, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { LangSwitcher } from '@/components/lang-switcher'
import { ONBOARDING_STEP_COUNT } from '@/hooks/use-onboarding-mode'
import { useSignedIn } from '@/hooks/use-signed-in'

const WWW_URL = import.meta.env.VITE_SITE_URL ?? 'https://goaltree.ai'

export function OnboardingProgress({
  step,
  partial,
  total = ONBOARDING_STEP_COUNT,
  className,
}: {
  step: number
  // Fill fraction of the current segment, for steps with sub-steps (e.g. Coach me).
  partial?: number
  total?: number
  className?: string
}) {
  const { t } = useTranslation()
  return (
    <div
      role="progressbar"
      aria-valuemin={1}
      aria-valuemax={total}
      aria-valuenow={step}
      aria-label={t('planner.step', { current: step, total })}
      className={cn('flex gap-1', className)}
    >
      {Array.from({ length: total }, (_, i) => (
        <span key={i} className="relative h-[3px] flex-1 overflow-hidden rounded-full bg-border">
          <span
            className="absolute inset-y-0 left-0 rounded-full bg-primary transition-[width] duration-300"
            style={{
              width: i < step - 1 ? '100%' : i === step - 1 ? `${(partial ?? 1) * 100}%` : '0%',
            }}
          />
        </span>
      ))}
    </div>
  )
}

// Replaces the app chrome while onboarding: logo, language, and the step bar.
export function OnboardingHeader({
  step,
  partial,
  total,
  onBack,
  rightSlot,
}: {
  step: number
  partial?: number
  total?: number
  onBack?: () => void
  rightSlot?: ReactNode
}) {
  const { t } = useTranslation()
  const auth = useSignedIn()
  const logo = <span className="font-logo text-xl font-extrabold tracking-tight">Goaltree</span>

  return (
    <header className="relative z-10 shrink-0 pt-[env(safe-area-inset-top,0px)]">
      <div className="flex h-14 items-center justify-between gap-3 px-4 lg:px-6">
        <div className="flex items-center gap-1">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              aria-label={t('common.back')}
              className="-ml-2 flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}
          {auth === 'out' ? <a href={WWW_URL}>{logo}</a> : logo}
        </div>
        <div className="flex items-center gap-2">
          {rightSlot}
          <LangSwitcher />
          {auth === 'out' && (
            <Link
              to="/auth/login"
              className="text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground"
            >
              {t('auth.signIn')}
            </Link>
          )}
        </div>
      </div>
      <OnboardingProgress
        step={step}
        partial={partial}
        total={total}
        className="mx-auto w-full max-w-xl px-4"
      />
    </header>
  )
}

// The new-goal flow's trail for returning users (first-timers get the bar in
// OnboardingHeader). Finished steps are links back.
const GOAL_STEPS = ['goal', 'questions', 'plan'] as const

export function GoalFlowSteps({
  step,
  onStep,
  className,
}: {
  step: number
  onStep?: (step: number) => void
  className?: string
}) {
  const { t } = useTranslation()
  return (
    <nav
      aria-label={t('planner.step', { current: step, total: GOAL_STEPS.length })}
      className={cn('flex items-center gap-1.5 text-xs font-medium', className)}
    >
      {GOAL_STEPS.map((key, i) => {
        const n = i + 1
        const label = t(`planner.steps.${key}`)
        return (
          <Fragment key={key}>
            {i > 0 && <ChevronRight className="size-3 text-muted-foreground/40" />}
            {n < step ? (
              <button
                type="button"
                onClick={() => onStep?.(n)}
                disabled={!onStep}
                className="flex cursor-pointer items-center gap-1 rounded-md text-muted-foreground transition-colors hover:text-foreground disabled:cursor-default"
              >
                <Check className="size-3.5 text-primary-text" strokeWidth={3} />
                {label}
              </button>
            ) : n === step ? (
              <span aria-current="step" className="flex items-center gap-1.5 text-foreground">
                <span className="size-1.5 rounded-full bg-primary" />
                {label}
              </span>
            ) : (
              <span className="text-muted-foreground/50">{label}</span>
            )}
          </Fragment>
        )
      })}
    </nav>
  )
}
